import React, { useMemo, useEffect } from 'react'

import { useClosuresEditMap } from '../../roadClosures/hooks'
import { useEsri } from '../hooks'

import GraphicLayer from './GraphicLayer'

import { ROADS_LAYER_URL } from '../constants'

const esriReqs = ['esri/layers/FeatureLayer']

const getClosureSymbol = (hovered) =>
  hovered
    ? {
      type: 'simple-line', // autocasts as new SimpleLineSymbol()
      color: 'green',
      width: '3px',
      style: 'solid',
    }
    : {
      type: 'simple-line', // autocasts as new SimpleLineSymbol()
      color: 'red',
      width: '3px',
      style: 'solid',
    }

const GISFeatureToRoadSegment = (feature) => {
  const {
    OBJECTID,
    FULLNAME,
    LEFTADDFROM,
    LEFTADDTO,
    RIGHTADDFROM,
    RIGHTADDTO,
    LeftCity,
    RightCity,
  } = feature.attributes
  const lowAddress = Math.min(
    Math.min(LEFTADDFROM || 0, LEFTADDTO || 0),
    Math.min(RIGHTADDFROM || 0, RIGHTADDTO || 0)
  )
  const highAddress = Math.max(
    Math.max(LEFTADDFROM || 0, LEFTADDTO || 0),
    Math.max(RIGHTADDFROM || 0, RIGHTADDTO || 0)
  )
  return {
    id: OBJECTID,
    name: FULLNAME,
    block: `${lowAddress} ${highAddress}`,
    city: LeftCity === RightCity ? LeftCity : `${LeftCity} ${RightCity}`,
    geometry: feature.geometry,
  }
}

const LAYER_ID = 'RoadClosuresEditLayer'

const RoadClosuresEditLayer = ({ map, view }) => {
  const [ready, { FeatureLayer }] = useEsri(esriReqs)
  const [
    { segments, editorIsOpen },
    { addSegments, removeSegments },
  ] = useClosuresEditMap()

  const roadsLayer = useMemo(() => {
    if (ready && FeatureLayer) {
      return new FeatureLayer({ url: ROADS_LAYER_URL })
    } else {
      return null
    }
  }, [ready, FeatureLayer])

  const graphics = useMemo(
    () =>
      editorIsOpen
        ? (segments || [])
          .filter((segment) => segment && !segment.removed)
          .map((segment) => ({
            geometry: {
              paths: segment.geometry.paths,
              spatialReference: segment.geometry.spatialReference,
              type: 'polyline',
            },
            symbol: getClosureSymbol(segment.hover),
            attributes: {
              layerId: LAYER_ID,
              segment,
            },
          }))
        : [],
    [segments, editorIsOpen]
  )

  // Set on click handler for layer
  useEffect(() => {
    if (view && editorIsOpen) {
      const handler = view.on('click', (event) => {
        const screenPoint = {
          x: event.x,
          y: event.y,
        }

        view.hitTest(screenPoint).then((response) => {
          // Remove Segment when you click on a segment in the layer
          if (response.results.length) {
            const matching = response.results.filter(
              (result) => result.graphic.attributes.layerId === LAYER_ID
            )
            if (matching.length) {
              removeSegments(
                matching.map(
                  (result) =>
                    result.graphic.attributes.data.attributes.segment.id
                )
              )
              return
            }
          }
          // If you click on something other than a closure segment,
          // search the roads layer for new segments to add
          if (roadsLayer) {
            const query = roadsLayer.createQuery()
            query.geometry = {
              type: 'point',
              x: event.mapPoint.longitude,
              y: event.mapPoint.latitude,
              spatialReference: { wkid: 4326 },
            }
            query.distance = 10
            query.units = 'meters'
            query.outFields = ['*']

            roadsLayer.queryFeatures(query).then((response) => {
              if (response.features && response.features.length > 0) {
                addSegments(response.features.map(GISFeatureToRoadSegment))
              }
            })
          }
        })
      })
      return handler.remove
    }
  }, [view, editorIsOpen, roadsLayer, addSegments, removeSegments])

  return (
    <GraphicLayer id={LAYER_ID} map={map} view={view} graphics={graphics} />
  )
}

export default RoadClosuresEditLayer
